import type {
  AccountCoverage,
  StatementCoverage,
} from '../StatementCoverage/types'

const toPercent = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 100) : 0

// A member card has no statements of its own; its months mirror the primary's, so counting it
// would report the same months once per card.
const primaryAccounts = (
  coverage: StatementCoverage | null,
): AccountCoverage[] =>
  (coverage?.accounts ?? []).filter((account) => !account.parentId)

// Shared by the masthead chip and the coverage ring so the two never disagree.
export interface CoverageSummary {
  covered: number
  gaps: number
  totalMonths: number
  percent: number
  incompleteAccounts: number
  // Primary accounts only; member cards ride their primary's statements.
  accountCount: number
  isComplete: boolean
}

export const summarizeCoverage = (
  coverage: StatementCoverage | null,
): CoverageSummary => {
  let covered = 0
  let gaps = 0
  let incompleteAccounts = 0
  let accountCount = 0
  for (const account of primaryAccounts(coverage)) {
    accountCount += 1
    const missing = account.statusCounts.missing ?? 0
    covered += account.monthlyStatus.length - missing
    gaps += missing
    if (missing > 0) incompleteAccounts += 1
  }
  const totalMonths = covered + gaps
  return {
    covered,
    gaps,
    totalMonths,
    percent: toPercent(covered, totalMonths),
    incompleteAccounts,
    accountCount,
    isComplete: totalMonths > 0 && gaps === 0,
  }
}

export interface ReconciliationSummary {
  reconciled: number
  // Months a statement covers. Gap months have no statement to reconcile, so they stay out.
  statementMonths: number
  percent: number
  isComplete: boolean
}

export const summarizeReconciliation = (
  coverage: StatementCoverage | null,
): ReconciliationSummary => {
  let reconciled = 0
  let statementMonths = 0
  for (const { statusCounts } of primaryAccounts(coverage)) {
    reconciled += statusCounts.reconciled
    statementMonths +=
      statusCounts.reconciled +
      statusCounts.needs_review +
      statusCounts.unverifiable +
      statusCounts.not_checked
  }
  return {
    reconciled,
    statementMonths,
    percent: toPercent(reconciled, statementMonths),
    isComplete: statementMonths > 0 && reconciled === statementMonths,
  }
}
